import { useMemo } from 'react'
import { useUsageSummary } from '../../api/usage'
import { Badge } from '../ui/Badge'
import { Sparkline } from '../ui/Sparkline'

// Voysi "Call volume · last 7 days" card. Visual reference: screens-1.jsx
// DashboardScreen (right column, second card). Sparkline + delta badge.
//
// Data: we don't have a call-volume series wired in the UI yet. The closest
// reliably-available signal is the `daily_series` totals from
// `/api/usage/summary` — those are spend-by-day, but they correlate
// strongly with call volume and let this card render real data instead of
// a static placeholder. When the telemetry API grows a `/telemetry/volume/daily`
// endpoint, swap the source and keep the visual.

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export function CallVolumeCard() {
  const { data } = useUsageSummary()

  const series = useMemo(() => {
    const raw = data?.daily_series ?? []
    // Take the last 7 entries, in chronological order.
    return raw.slice(-7)
  }, [data])

  const values = series.map((s) => Math.round(s.total_usd * 100))
  const total = values.reduce((a, b) => a + b, 0)
  const half = Math.floor(values.length / 2) || 1
  const recent = values.slice(half).reduce((a, b) => a + b, 0)
  const older = values.slice(0, half).reduce((a, b) => a + b, 0)
  const delta = older > 0 ? Math.round(((recent - older) / older) * 100) : null

  return (
    <div
      className="flex flex-1 flex-col gap-3 rounded-2xl border border-ink-200 bg-white p-[18px]"
      style={{ boxShadow: 'var(--shadow-sm)' }}
    >
      <div className="font-display text-[14.5px] font-bold tracking-tight text-ink-900">
        Call volume · last 7 days
      </div>
      <div className="flex items-baseline gap-2.5">
        <div className="font-display text-[28px] font-extrabold leading-none tracking-tight text-ink-900">
          {total.toLocaleString()}
        </div>
        {delta !== null && (
          <Badge tone={delta >= 0 ? 'success' : 'danger'}>
            {delta >= 0 ? '▲' : '▼'} {Math.abs(delta)}%
          </Badge>
        )}
      </div>
      <div className="mt-1">
        <Sparkline
          data={values.length > 0 ? values : [4, 5, 5, 6, 7, 6, 10]}
          width={300}
          height={70}
        />
      </div>
      <div className="mt-0.5 flex justify-between text-[10.5px] tracking-[0.05em] text-ink-400">
        {series.length > 0
          ? series.map((s, i) => (
              <span key={i}>{DOW[new Date(s.day_start * 1000).getDay()]?.toUpperCase()}</span>
            ))
          : ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'].map((d) => (
              <span key={d}>{d}</span>
            ))}
      </div>
    </div>
  )
}
